import type { CreateMassagerFormData } from "@/schemas/createMassagerSchema";
import type { MassagerDetail } from "@/services/admin/massagers";
import { MAX_INSURER_ROWS } from '@/constants';
import { normalizeLocationId } from '@/utils/locationId';

const AVAILABLE_TO_INDEFINITE = '2100-12-31';

/** Treat backend sentinel "2100-12-31" (indefinite) as empty so the date picker shows nothing. */
const stripIndefiniteAvailableTo = (value: string | null | undefined): string | null => {
  if (value == null) return null;
  return value.slice(0, 10) === AVAILABLE_TO_INDEFINITE ? null : value;
};

const emptyWideAreaRow = {
  wide_area_union_code: null as string | null,
  wide_area_union_number: null as string | null,
  prefecture_number: null as string | null,
};

/**
 * Maps API massager detail (show or copy) to create/edit form default values.
 * RA2: 保険者別番号 — existing rows + 1 empty row at bottom.
 */
export function mapDetailToFormData(
  d: MassagerDetail
): Partial<CreateMassagerFormData> {
  const existingRows = d.wide_area_union_rows?.length
    ? d.wide_area_union_rows.slice(0, MAX_INSURER_ROWS)
    : d.wide_area_union_code != null ||
        d.wide_area_union_number != null ||
        d.prefecture_number != null
      ? [
          {
            wide_area_union_code: d.wide_area_union_code ?? null,
            wide_area_union_number: d.wide_area_union_number ?? null,
            prefecture_number: d.prefecture_number ?? null,
          },
        ]
      : [];
  const wide_area_union_rows =
    existingRows.length < MAX_INSURER_ROWS ? [...existingRows, { ...emptyWideAreaRow }] : existingRows;

  /**
   * Map all A2 and R2 fields from API so create/edit form can show the correct layout
   * (A2 only, R2 only, or BOTH) and prefill both columns when editing on BOTH clinic.
   * API returns clinic_location_ids_* as array of { id, available_from, available_to }.
   */
  const idsA2 = d.clinic_location_ids_a2 ?? [];
  const idsR2 = d.clinic_location_ids_r2 ?? [];
  const toLocationRow = (
    item:
      | number
      | {
          id?: number;
          available_from?: string | null;
          available_to?: string | null;
          regist_date?: string | null;
          stop_request_date?: string | null;
        }
  ) => {
    if (typeof item === 'number') {
      return {
        clinic_location_id: normalizeLocationId(item),
        available_from: null,
        available_to: null,
        regist_date: null,
        stop_request_date: null,
      };
    }
    return {
      clinic_location_id: normalizeLocationId(item.id ?? 0),
      available_from: (item.available_from ?? null) as string | null,
      available_to: stripIndefiniteAvailableTo(item.available_to),
      regist_date: (item.regist_date ?? null) as string | null,
      stop_request_date: (item.stop_request_date ?? null) as string | null,
    };
  };
  const clinic_locations_a2 =
    idsA2.length > 0
      ? idsA2.map(toLocationRow)
      : [{ clinic_location_id: 0, available_from: null, available_to: null, regist_date: null, stop_request_date: null }];
  const clinic_locations_r2 =
    idsR2.length > 0
      ? idsR2.map(toLocationRow)
      : [{ clinic_location_id: 0, available_from: null, available_to: null, regist_date: null, stop_request_date: null }];

  return {
    clinic_id: d.clinic_id,
    clinic_locations_a2,
    clinic_locations_r2,
    name: d.name,
    name_kana: d.name_kana ?? null,
    login_id: d.login_id ?? '',
    password: d.password ?? '',
    email: d.email ?? null,
    acupuncture_license_number: d.acupuncture_license_number ?? null,
    moxibustion_license_number: d.moxibustion_license_number ?? null,
    license_number: d.license_number ?? null,
    wide_area_union_rows,
    massage_wide_area_union_rows: buildMassageWideAreaRows(d.massage_wide_area_union_rows),
  };
}

function buildMassageWideAreaRows(
  rows: Array<{ wide_area_union_code?: string | null; wide_area_union_number?: string | null; prefecture_number?: string | null }> | null | undefined
): Array<{ wide_area_union_code: string | null; wide_area_union_number: string | null; prefecture_number: string | null }> {
  if (rows?.length) {
    const normalized = rows
      .slice(0, MAX_INSURER_ROWS)
      .map(r => ({ ...emptyWideAreaRow, ...r }));
    return normalized.length < MAX_INSURER_ROWS ? [...normalized, { ...emptyWideAreaRow }] : normalized;
  }
  return [{ ...emptyWideAreaRow }];
}
